/**
 * Responsibility: say how far along a grouping is, when that can be known.
 *
 * Shown only while a sub-collection or a set is being filtered on, because
 * "47 cards" against a whole collection is the figure already at the top of
 * the page and repeating it says nothing.
 *
 * **Both can show a real fraction, and a set often cannot.** A tag's goal is a
 * number somebody typed, so it is exactly true. A set's total is counted out of
 * the PSA population universe and is exact on eight of nine sets measured - but
 * it is absent whenever that count cannot be trusted: promo buckets that grow
 * forever, sports cards that are not in a Pokemon population file, and set
 * names too ambiguous to match safely.
 *
 * When the total is missing the panel says so and shows what is certain. A
 * wrong denominator would be worse than none: "31 of 102" on a set that holds
 * 165 reads as three quarters done and nobody would think to check it.
 */
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import LinearProgress from '@mui/material/LinearProgress';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import Button from '@mui/material/Button';

import type { CollectionItem, CollectionTag, SetSummary } from '../../api/client';
import { cardNumber } from './cardNumber';

type Props = {
  /** The holdings currently shown, already filtered. */
  items: CollectionItem[];
  /** The sub-collection being filtered on, if any. */
  tag: CollectionTag | null;
  /** The set label being filtered on, if any. */
  set: string;
  /** What is known about that set's size, when anything is. */
  setSummary?: SetSummary | null;
  /** Opens the full-set view, listing what is still missing. */
  onOpenSet?: (label: string) => void;
};

/** How many separate cards these holdings represent, ignoring duplicates. */
function distinctCards(items: CollectionItem[]): number {
  return new Set(items.map((item) => item.product_key)).size;
}

function TagProgress({ tag, items }: { tag: CollectionTag; items: CollectionItem[] }) {
  const held = distinctCards(items);
  // The goal counts cards, so duplicates of one card are one card towards it.
  // Counting holdings would let three copies of the same card read as three
  // steps along a run that has only moved one.
  const percent = tag.goal ? Math.min(100, Math.round((held / tag.goal) * 100)) : null;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {tag.name}
        </Typography>
        {tag.goal ? (
          <Typography variant="body2" color="text.secondary">
            {held} of {tag.goal} cards &middot; {percent}%
          </Typography>
        ) : (
          <Typography variant="body2" color="text.secondary">
            {held} {held === 1 ? 'card' : 'cards'} &middot; no target set
          </Typography>
        )}
      </Stack>

      {tag.note ? (
        <Typography variant="body2" color="text.secondary">
          {tag.note}
        </Typography>
      ) : null}

      {percent !== null ? (
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{ height: 8, borderRadius: 1 }}
        />
      ) : null}
    </Stack>
  );
}

function SetProgress({
  set,
  items,
  summary,
  onOpen,
}: {
  set: string;
  items: CollectionItem[];
  summary: SetSummary | null;
  onOpen?: (label: string) => void;
}) {
  const held = distinctCards(items);
  const total = summary?.total ?? null;
  const percent = total ? Math.min(100, Math.round((held / total) * 100)) : null;
  const numbers = items
    .map((item) => cardNumber(item.product_key))
    .filter((value): value is number => value !== null);
  const highest = numbers.length > 0 ? Math.max(...numbers) : null;

  return (
    <Stack spacing={1}>
      <Stack direction="row" spacing={1} alignItems="baseline" flexWrap="wrap" useFlexGap>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {set}
        </Typography>
        {total !== null ? (
          <Typography variant="body2" color="text.secondary">
            {held} of {total} cards &middot; {percent}%
          </Typography>
        ) : null}
        {summary?.year ? (
          <Typography variant="caption" color="text.secondary">
            {summary.year}
          </Typography>
        ) : null}
      </Stack>

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
        <Chip size="small" label={`${held} cards held`} />
        {items.length !== held ? (
          <Chip size="small" variant="outlined" label={`${items.length} holdings`} />
        ) : null}
        {total === null && highest !== null ? (
          <Chip size="small" variant="outlined" label={`highest number #${highest}`} />
        ) : null}
        {total !== null && onOpen ? (
          <Button size="small" variant="outlined" onClick={() => onOpen(set)}>
            See the full set
          </Button>
        ) : null}
      </Stack>

      {percent !== null ? (
        <LinearProgress
          variant="determinate"
          value={percent}
          sx={{ height: 8, borderRadius: 1 }}
        />
      ) : (
        <Typography variant="caption" color="text.secondary">
          No total for this set. It is either a grouping that grows forever, a
          card this system has no population data for, or a name too ambiguous
          to match safely &mdash; and a guessed denominator would make a third of
          a set look like most of one.
        </Typography>
      )}
    </Stack>
  );
}

export function CollectionProgress({ items, tag, set, setSummary, onOpenSet }: Props) {
  if (tag === null && !set) return null;

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box sx={{ minWidth: 0 }}>
        {tag !== null ? (
          <TagProgress tag={tag} items={items} />
        ) : (
          <SetProgress
            set={set}
            items={items}
            summary={setSummary ?? null}
            onOpen={onOpenSet}
          />
        )}
      </Box>
    </Paper>
  );
}
